import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	FC,
	ReactNode,
} from 'react'
import { WARNING_NOTE_SUFFIX } from '../../libs/inputalert'

type FormikSetField = (fieldKey: string, note: string) => Promise<any>
type InputSave = (value: string) => Promise<any>
export interface WarningNoteDialogState {
	instrument: string
	key: string
	isOpen: boolean
	setFieldValue?: FormikSetField | InputSave
	index?: number
}

interface InputAlertWarningNoteContextType {
	dialogs: WarningNoteDialogState[]
	notes: Record<string, string>
	openDialog: (
		instrument: string,
		key: string,
		setFieldValue: any,
		index?: number,
	) => void
	closeDialog: (instrument: string, key: string) => void
	removeDialog: (instrument: string, key: string) => void
	updateNote: (instrument: string, key: string, note: string) => void
	getNote: (instrument: string, key: string) => string
	saveNote: (instrument: string, key: string, note: string) => void
}

const InputAlertWarningNoteContext = createContext<
	InputAlertWarningNoteContextType | undefined
>(undefined)

export const InputAlertWarningNoteProvider: FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [dialogs, setDialogs] = useState<WarningNoteDialogState[]>([])
	const [notes, setNotes] = useState<Record<string, string>>({})

	const openDialog = useCallback(
		(instrument: string, key: string, setFieldValue: any, index?: number) => {
			setDialogs(prev => {
				const exists = prev.find(
					d => d.instrument === instrument && d.key === key,
				)
				if (exists) {
					return prev.map(d =>
						d.instrument === instrument && d.key === key
							? { ...d, isOpen: true, setFieldValue, index }
							: d,
					)
				}
				return [
					...prev,
					{ instrument, key, isOpen: true, setFieldValue, index },
				]
			})
		},
		[],
	)

	const closeDialog = useCallback((instrument: string, key: string) => {
		setDialogs(prev =>
			prev.map(d =>
				d.instrument === instrument && d.key === key
					? { ...d, isOpen: false }
					: d,
			),
		)
	}, [])

	const removeDialog = useCallback((instrument: string, key: string) => {
		setDialogs(prev =>
			prev.filter(d => !(d.instrument === instrument && d.key === key)),
		)
		setNotes(prev => {
			const newNotes = { ...prev }
			delete newNotes[`${instrument}-${key}`]
			return newNotes
		})
	}, [])

	const updateNote = useCallback(
		(instrument: string, key: string, note: string) => {
			const dialogId = `${instrument}-${key}`
			setNotes(prev => ({ ...prev, [dialogId]: note }))
		},
		[],
	)

	const getNote = useCallback(
		(instrument: string, key: string) => {
			const dialogId = `${instrument}-${key}`
			return notes[dialogId] || ''
		},
		[notes],
	)

	const saveNote = useCallback(
		async (instrument: string, key: string, note: string) => {
			const dialog = dialogs.find(
				d => d.instrument === instrument && d.key === key,
			)
			if (dialog?.setFieldValue) {
				const fieldKey = `${key}${WARNING_NOTE_SUFFIX}`
				const { length } = dialog.setFieldValue
				if ([0, 2].includes(length)) {
					await dialog.setFieldValue(fieldKey, note)
				} else {
					await (dialog.setFieldValue as InputSave)(note)
				}
			}
			removeDialog(instrument, key)
		},
		[dialogs, removeDialog],
	)

	const value: InputAlertWarningNoteContextType = {
		dialogs,
		notes,
		openDialog,
		closeDialog,
		removeDialog,
		updateNote,
		getNote,
		saveNote,
	}

	return (
		<InputAlertWarningNoteContext.Provider value={value}>
			{children}
		</InputAlertWarningNoteContext.Provider>
	)
}

const defaultContextValue: InputAlertWarningNoteContextType = {
	dialogs: [],
	notes: {},
	openDialog: () => {},
	closeDialog: () => {},
	removeDialog: () => {},
	updateNote: () => {},
	getNote: () => '',
	saveNote: async () => {},
}

export const useInputAlertWarningNoteDialog = () => {
	const context = useContext(InputAlertWarningNoteContext)
	return context || defaultContextValue
}
